/** Lifecycle states. Each status lists the facts consumers need, so nobody switches on literals. */

export const SCREENING_STATUSES = ['open', 'ended'] as const;
export type ScreeningStatus = (typeof SCREENING_STATUSES)[number];

export interface ScreeningStatusDefinition {
  /** Guests may join and receive an RTC token. */
  readonly admitsParticipants: boolean;
  /** Playback, chat and moderation commands are accepted. */
  readonly acceptsCommands: boolean;
}

export const SCREENING_STATUS_DEFINITIONS = {
  open: { admitsParticipants: true, acceptsCommands: true },
  ended: { admitsParticipants: false, acceptsCommands: false },
} as const satisfies Record<ScreeningStatus, ScreeningStatusDefinition>;

export const ASSET_STATUSES = ['awaiting_upload', 'queued', 'processing', 'ready', 'failed'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export interface AssetStatusDefinition {
  /** Can be attached to a screening and played. */
  readonly playable: boolean;
  /** Work is still in progress; clients should poll for changes. */
  readonly inProgress: boolean;
  /** The source file has not fully arrived; a host can (re)send it through the tus endpoint. */
  readonly awaitsUpload: boolean;
}

export const ASSET_STATUS_DEFINITIONS = {
  awaiting_upload: { playable: false, inProgress: true, awaitsUpload: true },
  queued: { playable: false, inProgress: true, awaitsUpload: false },
  processing: { playable: false, inProgress: true, awaitsUpload: false },
  ready: { playable: true, inProgress: false, awaitsUpload: false },
  failed: { playable: false, inProgress: false, awaitsUpload: false },
} as const satisfies Record<AssetStatus, AssetStatusDefinition>;

export const TRACK_STATUSES = ['processing', 'ready', 'failed'] as const;
export type TrackStatus = (typeof TRACK_STATUSES)[number];

export interface TrackStatusDefinition {
  /** Conversion is still running; clients should poll for changes. */
  readonly inProgress: boolean;
}

export const TRACK_STATUS_DEFINITIONS = {
  processing: { inProgress: true },
  ready: { inProgress: false },
  failed: { inProgress: false },
} as const satisfies Record<TrackStatus, TrackStatusDefinition>;

/** Why media processing failed. Shown to hosts through translations keyed by the code. */
export const MEDIA_FAILURE_CODES = [
  'upload_incomplete',
  'probe_failed',
  'no_video_stream',
  'transcode_failed',
  'subtitle_unsupported',
  'subtitle_convert_failed',
] as const;
export type MediaFailureCode = (typeof MEDIA_FAILURE_CODES)[number];
